import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import TransactionLogger from '../../utils/transactionLogger';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  QrCode,
  DeleteSweep,
  FileUpload,
  Download
} from '@mui/icons-material';
import QRCode from 'react-qr-code';

export default function PlayerList() {
  const { userProfile, currentUser, loading: authLoading } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Check permissions
  const canDeleteAllPlayers = hasPermission(userProfile?.role, PERMISSIONS.DELETE_ALL_PLAYERS);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [qrCodePlayer, setQrCodePlayer] = useState(null);
  
  // Debug logging
  console.log('PlayerList - currentUser:', currentUser);
  console.log('PlayerList - userProfile:', userProfile);
  console.log('PlayerList - authLoading:', authLoading);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    handicap: '',
    membershipNumber: '',
    division: 'Open'
  });

  useEffect(() => {
    if (userProfile && userProfile.organizationName) {
      fetchPlayers();
    }
  }, [userProfile]);

  const fetchPlayers = async () => {
    try {
      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', userProfile?.organizationName)
      );
      const snapshot = await getDocs(playersQuery);
      const playersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    console.log('User profile:', userProfile);
    console.log('Auth loading:', authLoading);
    console.log('Current user:', currentUser);
    
    // Check if auth is still loading
    if (authLoading) {
      alert('Authentication is still loading. Please wait...');
      return;
    }
    
    // Check if userProfile is available
    if (!userProfile) {
      alert('User profile not loaded. Please try again. Current user: ' + (currentUser ? 'Yes' : 'No') + '. Auth loading: ' + authLoading);
      return;
    }
    
    // Check if organizationName is available
    if (!userProfile.organizationName) {
      alert('Organization name not found in user profile. Please contact support.');
      return;
    }
    
    // Check if required fields are filled
    if (!formData.firstName || !formData.lastName || !formData.email) {
      alert('Please fill in all required fields (First Name, Last Name, Email).');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const playerData = {
        ...formData,
        firstName: formatName(formData.firstName),
        lastName: formatName(formData.lastName),
        organizationId: userProfile.organizationName,
        createdAt: new Date(),
        handicap: parseFloat(formData.handicap) || 0
      };

      console.log('Player data to save:', playerData);

      if (editingPlayer) {
        console.log('Updating existing player:', editingPlayer.id);
        const oldData = { ...editingPlayer };
        await updateDoc(doc(db, 'players', editingPlayer.id), playerData);
        setPlayers(players.map(p => p.id === editingPlayer.id ? { ...p, ...playerData } : p));
        
        // Log transaction
        await TransactionLogger.logTransaction(
          'UPDATE',
          'PLAYER',
          editingPlayer.id,
          oldData,
          playerData,
          currentUser.uid,
          userProfile.email
        );
      } else {
        console.log('Adding new player to database');
        const docRef = await addDoc(collection(db, 'players'), playerData);
        console.log('Player added with ID:', docRef.id);
        setPlayers([...players, { id: docRef.id, ...playerData }]);
        
        // Log transaction
        await TransactionLogger.logTransaction(
          'CREATE',
          'PLAYER',
          docRef.id,
          null,
          { id: docRef.id, ...playerData },
          currentUser.uid,
          userProfile.email
        );
      }

      console.log('Player saved successfully');
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving player:', error);
      alert('Error saving player: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (playerId) => {
    if (window.confirm('Are you sure you want to delete this player?')) {
      try {
        const playerToDelete = players.find(p => p.id === playerId);
        await deleteDoc(doc(db, 'players', playerId));
        setPlayers(players.filter(p => p.id !== playerId));
        
        // Log transaction
        await TransactionLogger.logTransaction(
          'DELETE',
          'PLAYER',
          playerId,
          playerToDelete,
          null,
          currentUser.uid,
          userProfile.email
        );
      } catch (error) {
        console.error('Error deleting player:', error);
      }
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setFormData({
      firstName: formatName(player.firstName),
      lastName: formatName(player.lastName),
      email: player.email,
      phone: player.phone,
      handicap: player.handicap,
      membershipNumber: player.membershipNumber,
      division: player.division || 'Open'
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPlayer(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      handicap: '',
      membershipNumber: '',
      division: 'Open'
    });
  };

  // Utility function to format names with proper capitalization
  const formatName = (name) => {
    if (!name) return '';
    return name.toLowerCase().split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const generateQRCode = (player) => {
    if (!player) {
      return '';
    }
    const qrData = {
      playerId: player.id,
      playerName: `${player.firstName} ${player.lastName}`,
      organizationId: player.organizationId,
      timestamp: new Date().toISOString()
    };
    return JSON.stringify(qrData);
  };

  // Delete all players function
  const handleDeleteAll = async () => {
    if (window.confirm(`Are you sure you want to delete ALL ${players.length} players? This action cannot be undone.`)) {
      try {
        setSubmitting(true);
        const deletePromises = players.map(player => 
          deleteDoc(doc(db, 'players', player.id))
        );
        await Promise.all(deletePromises);
        
        // Log transaction for each deleted player
        const logPromises = players.map(player => 
          TransactionLogger.logTransaction(
            'DELETE',
            'PLAYER',
            player.id,
            player,
            null,
            currentUser.uid,
            userProfile.email
          )
        );
        await Promise.all(logPromises);
        
        setPlayers([]);
        alert('All players have been deleted successfully.');
      } catch (error) {
        console.error('Error deleting all players:', error);
        alert('Error deleting players: ' + error.message);
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Download sample CSV function
  const downloadSampleCSV = () => {
    const csvContent = `firstName,lastName,email,phone,handicap,membershipNumber,division
John,Doe,john.doe@email.com,555-0123,12,12345,Open
Jane,Smith,jane.smith@email.com,555-0124,8,12346,Senior
Bob,Johnson,bob.johnson@email.com,555-0125,15,12347,Legend`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample_players.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // CSV import function
  const handleCSVImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        
        const playersToImport = [];
        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim()) {
            const values = lines[i].split(',').map(v => v.trim());
            const playerData = {
              firstName: formatName(values[0] || ''),
              lastName: formatName(values[1] || ''),
              email: values[2] || '',
              phone: values[3] || '',
              handicap: parseFloat(values[4]) || 0,
              membershipNumber: values[5] || '',
              division: values[6] || 'Open',
              organizationId: userProfile.organizationName,
              createdAt: new Date()
            };
            
            // Validate required fields
            if (playerData.firstName && playerData.lastName && playerData.email) {
              playersToImport.push(playerData);
            }
          }
        }

        if (playersToImport.length === 0) {
          alert('No valid players found in CSV file.');
          return;
        }

        // Add players to database
        setSubmitting(true);
        const addPromises = playersToImport.map(playerData => 
          addDoc(collection(db, 'players'), playerData)
        );
        const docRefs = await Promise.all(addPromises);
        
        // Update local state
        const newPlayers = playersToImport.map((playerData, index) => ({
          id: docRefs[index].id,
          ...playerData
        }));
        setPlayers([...players, ...newPlayers]);
        
        alert(`Successfully imported ${playersToImport.length} players.`);
      } catch (error) {
        console.error('Error importing CSV:', error);
        alert('Error importing CSV: ' + error.message);
      } finally {
        setSubmitting(false);
        // Reset file input
        event.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  if (authLoading) {
    return <Container><Typography>Loading authentication...</Typography></Container>;
  }

  if (!userProfile) {
    return (
      <Container>
        <Typography>Loading user profile...</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          If this takes too long, please try refreshing the page or contact support.
        </Typography>
      </Container>
    );
  }

  if (loading) {
    return <Container><Typography>Loading players...</Typography></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Players</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={downloadSampleCSV}
          >
            Sample CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileUpload />}
            component="label"
          >
            Import CSV
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={handleCSVImport}
            />
          </Button>
          {canDeleteAllPlayers && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweep />}
              onClick={handleDeleteAll}
              disabled={players.length === 0}
            >
              Delete All
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
          >
            Add Player
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Handicap</TableCell>
              <TableCell>Division</TableCell>
              <TableCell>Membership #</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {players.map((player) => (
              <TableRow key={player.id}>
                <TableCell>{player.firstName} {player.lastName}</TableCell>
                <TableCell>{player.email}</TableCell>
                <TableCell>{player.phone}</TableCell>
                <TableCell>
                  <Chip 
                    label={player.handicap} 
                    color={player.handicap < 10 ? 'success' : player.handicap < 20 ? 'warning' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={player.division || 'Open'} 
                    color={player.division === 'Open' ? 'primary' : player.division === 'Senior' ? 'secondary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{player.membershipNumber}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => setQrCodePlayer(player)}
                    title="Generate QR Code"
                  >
                    <QrCode />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(player)}
                    title="Edit Player"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(player.id)}
                    title="Delete Player"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Player Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPlayer ? 'Edit Player' : 'Add New Player'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                autoFocus
                margin="dense"
                label="First Name"
                fullWidth
                variant="outlined"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: formatName(e.target.value)})}
                required
              />
              <TextField
                margin="dense"
                label="Last Name"
                fullWidth
                variant="outlined"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: formatName(e.target.value)})}
                required
              />
            </Box>
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
            <TextField
              margin="dense"
              label="Phone"
              fullWidth
              variant="outlined"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                margin="dense"
                label="Handicap"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.handicap}
                onChange={(e) => setFormData({...formData, handicap: e.target.value})}
                inputProps={{ min: 0, max: 54, step: 0.1 }}
              />
              <TextField
                margin="dense"
                label="Membership Number"
                fullWidth
                variant="outlined"
                value={formData.membershipNumber}
                onChange={(e) => setFormData({...formData, membershipNumber: e.target.value})}
              />
            </Box>
            <FormControl component="fieldset" margin="normal" required>
              <FormLabel component="legend">Division</FormLabel>
              <RadioGroup
                row
                value={formData.division}
                onChange={(e) => setFormData({...formData, division: e.target.value})}
              >
                <FormControlLabel value="Open" control={<Radio />} label="Open" />
                <FormControlLabel value="Senior" control={<Radio />} label="Senior" />
                <FormControlLabel value="Legend" control={<Radio />} label="Legend" />
              </RadioGroup>
              <FormHelperText>Select the player's division</FormHelperText>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Saving...' : (editingPlayer ? 'Update' : 'Add') + ' Player'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!qrCodePlayer} onClose={() => setQrCodePlayer(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          QR Code for {qrCodePlayer?.firstName} {qrCodePlayer?.lastName}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" p={2}>
            {qrCodePlayer && (
              <>
                <QRCode value={generateQRCode(qrCodePlayer)} size={200} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Players can scan this code to sign in to tournaments
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrCodePlayer(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
